import { RowCrudStatus } from "../../common/types";
import {
  SatelliteMachine,
  SatelliteMachineCreateMutationVariables,
  SatelliteMachineUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { blankToNull } from "../../../common/blankToNull";
import { userDisplayId } from "../../user/store/UserCommonStore";
import { safeJsonParse } from "../../../common/utils";
import {
  SatelliteTaskValuesType,
  getSatelliteTaskValues,
} from "../../satelliteTask/store/SatelliteTaskCommonStore";

export type SatelliteMachineValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  name: string;
  machineCode: string;
  satelliteType: string;
  region: string;
  certificateFingerprint: string;
  certificateFingerprintHasValue: boolean;
  certificateExpiresAt: Date | null;
  authTokenHash: string;
  authTokenHashHasValue: boolean;
  authTokenLastFour: string;
  authTokenCreatedAt: Date | null;
  authTokenRotatedAt: Date | null;
  status: string;
  authorizedAt: Date | null;
  __authorizedBy: string;
  showAuthorizedBy: { displayId: string; id: string } | null;
  suspendedAt: Date | null;
  __suspendedBy: string;
  showSuspendedBy: { displayId: string; id: string } | null;
  suspensionReason: string;
  enabled: boolean;
  allowedTenantIds: string;
  proxyEnabled: boolean;
  proxyPort: number | undefined;
  proxyProtocols: string;
  botEnabled: boolean;
  botMaxConcurrent: number | undefined;
  workerEnabled: boolean;
  publicIp: string;
  location: string;
  countryCode: string;
  lastHeartbeatAt: Date | null;
  lastHeartbeatMetrics: string;
  connectedAt: Date | null;
  disconnectedAt: Date | null;
  tunnelConnectedAt: Date | null;
  showTasks: SatelliteTaskValuesType[];
};

export type SatelliteMachineErrorsType = {
  name?: { message: string; fieldLabel: string };
  machineCode?: { message: string; fieldLabel: string };
  satelliteType?: { message: string; fieldLabel: string };
  region?: { message: string; fieldLabel: string };
  certificateFingerprint?: { message: string; fieldLabel: string };
  certificateExpiresAt?: { message: string; fieldLabel: string };
  authTokenHash?: { message: string; fieldLabel: string };
  authTokenLastFour?: { message: string; fieldLabel: string };
  authTokenCreatedAt?: { message: string; fieldLabel: string };
  authTokenRotatedAt?: { message: string; fieldLabel: string };
  status?: { message: string; fieldLabel: string };
  authorizedAt?: { message: string; fieldLabel: string };
  authorizedBy?: { message: string; fieldLabel: string };
  suspendedAt?: { message: string; fieldLabel: string };
  suspendedBy?: { message: string; fieldLabel: string };
  suspensionReason?: { message: string; fieldLabel: string };
  enabled?: { message: string; fieldLabel: string };
  allowedTenantIds?: { message: string; fieldLabel: string };
  proxyEnabled?: { message: string; fieldLabel: string };
  proxyPort?: { message: string; fieldLabel: string };
  proxyProtocols?: { message: string; fieldLabel: string };
  botEnabled?: { message: string; fieldLabel: string };
  botMaxConcurrent?: { message: string; fieldLabel: string };
  workerEnabled?: { message: string; fieldLabel: string };
  publicIp?: { message: string; fieldLabel: string };
  location?: { message: string; fieldLabel: string };
  countryCode?: { message: string; fieldLabel: string };
  lastHeartbeatAt?: { message: string; fieldLabel: string };
  lastHeartbeatMetrics?: { message: string; fieldLabel: string };
  connectedAt?: { message: string; fieldLabel: string };
  disconnectedAt?: { message: string; fieldLabel: string };
  tunnelConnectedAt?: { message: string; fieldLabel: string };
  tasks?: { message: string; fieldLabel: string };
};

export function SatelliteMachineValuesInit(): SatelliteMachineValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    name: "",
    machineCode: "",
    satelliteType: "proxy",
    region: "",
    certificateFingerprint: "",
    certificateFingerprintHasValue: false,
    certificateExpiresAt: null,
    authTokenHash: "",
    authTokenHashHasValue: false,
    authTokenLastFour: "",
    authTokenCreatedAt: null,
    authTokenRotatedAt: null,
    status: "pending_authorization",
    authorizedAt: null,
    __authorizedBy: "",
    showAuthorizedBy: null,
    suspendedAt: null,
    __suspendedBy: "",
    showSuspendedBy: null,
    suspensionReason: "",
    enabled: false,
    allowedTenantIds: "",
    proxyEnabled: false,
    proxyPort: undefined,
    proxyProtocols: "",
    botEnabled: false,
    botMaxConcurrent: undefined,
    workerEnabled: false,
    publicIp: "",
    location: "",
    countryCode: "",
    lastHeartbeatAt: null,
    lastHeartbeatMetrics: "{}",
    connectedAt: null,
    disconnectedAt: null,
    tunnelConnectedAt: null,
    showTasks: [],
  };
}

export type SatelliteMachineType =
  | SatelliteMachine
  | SatelliteMachineValuesType;

export function satelliteMachineDisplayId(
  source: Partial<SatelliteMachine>,
  _langStore: LangStore
) {
  return source.name ?? _langStore.emptyStringSymbol;
}

export function getSatelliteMachineValues(
  source: SatelliteMachineType,
  _langStore: LangStore,
  _duplicated: boolean = false
): SatelliteMachineValuesType {
  const entityValues = SatelliteMachineValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : satelliteMachineDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.name = source.name;
  entityValues.machineCode = source.machineCode;
  entityValues.satelliteType = source.satelliteType;
  entityValues.region = source.region || "";
  entityValues.certificateFingerprint = "";
  entityValues.certificateFingerprintHasValue =
    !!source.certificateFingerprintHasValue;
  entityValues.certificateExpiresAt = new Date(source.certificateExpiresAt);
  entityValues.authTokenHash = "";
  entityValues.authTokenHashHasValue = !!source.authTokenHashHasValue;
  entityValues.authTokenLastFour = source.authTokenLastFour;
  entityValues.authTokenCreatedAt = new Date(source.authTokenCreatedAt);
  entityValues.authTokenRotatedAt = source.authTokenRotatedAt
    ? new Date(source.authTokenRotatedAt)
    : null;
  entityValues.status = source.status;
  entityValues.authorizedAt = source.authorizedAt
    ? new Date(source.authorizedAt)
    : null;
  entityValues.showAuthorizedBy =
    "authorizedBy" in source && source.authorizedBy
      ? {
          id: source.authorizedBy.id,
          displayId: userDisplayId(source.authorizedBy, _langStore),
        }
      : "showAuthorizedBy" in source && source.showAuthorizedBy
      ? {
          id: source.showAuthorizedBy.id,
          displayId: source.showAuthorizedBy.displayId,
        }
      : null;
  entityValues.__authorizedBy = entityValues.showAuthorizedBy?.displayId || "";
  entityValues.suspendedAt = source.suspendedAt
    ? new Date(source.suspendedAt)
    : null;
  entityValues.showSuspendedBy =
    "suspendedBy" in source && source.suspendedBy
      ? {
          id: source.suspendedBy.id,
          displayId: userDisplayId(source.suspendedBy, _langStore),
        }
      : "showSuspendedBy" in source && source.showSuspendedBy
      ? {
          id: source.showSuspendedBy.id,
          displayId: source.showSuspendedBy.displayId,
        }
      : null;
  entityValues.__suspendedBy = entityValues.showSuspendedBy?.displayId || "";
  entityValues.suspensionReason = source.suspensionReason || "";
  entityValues.enabled = source.enabled || false;
  entityValues.allowedTenantIds = source.allowedTenantIds || "";
  entityValues.proxyEnabled = source.proxyEnabled || false;
  entityValues.proxyPort =
    (source.proxyPort as unknown) === "" ||
    source.proxyPort === undefined ||
    source.proxyPort === null
      ? undefined
      : source.proxyPort;
  entityValues.proxyProtocols = source.proxyProtocols || "";
  entityValues.botEnabled = source.botEnabled || false;
  entityValues.botMaxConcurrent =
    (source.botMaxConcurrent as unknown) === "" ||
    source.botMaxConcurrent === undefined ||
    source.botMaxConcurrent === null
      ? undefined
      : source.botMaxConcurrent;
  entityValues.workerEnabled = source.workerEnabled || false;
  entityValues.publicIp = source.publicIp || "";
  entityValues.location = source.location || "";
  entityValues.countryCode = source.countryCode || "";
  entityValues.lastHeartbeatAt = source.lastHeartbeatAt
    ? new Date(source.lastHeartbeatAt)
    : null;
  entityValues.lastHeartbeatMetrics = source.lastHeartbeatMetrics || "";
  entityValues.connectedAt = source.connectedAt
    ? new Date(source.connectedAt)
    : null;
  entityValues.disconnectedAt = source.disconnectedAt
    ? new Date(source.disconnectedAt)
    : null;
  entityValues.tunnelConnectedAt = source.tunnelConnectedAt
    ? new Date(source.tunnelConnectedAt)
    : null;
  entityValues.showTasks =
    "tasks" in source && source.tasks
      ? (source.tasks || []).map((x) => {
          const row: SatelliteTaskValuesType = getSatelliteTaskValues(
            x,
            _langStore,
            _duplicated
          );
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showTasks" in source && source.showTasks
      ? (source.showTasks || []).map((x) => {
          const row: SatelliteTaskValuesType = getSatelliteTaskValues(
            x,
            _langStore,
            _duplicated
          );
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getSatelliteMachineUpdateVariables(
  values: SatelliteMachineValuesType
): SatelliteMachineUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    name: values.name,
    machineCode: values.machineCode,
    satelliteType: values.satelliteType,
    region: blankToNull(values.region),
    certificateFingerprint: values.certificateFingerprint || undefined,
    certificateExpiresAt: values.certificateExpiresAt!.toISOString(),
    authTokenHash: values.authTokenHash || undefined,
    authTokenLastFour: values.authTokenLastFour,
    authTokenCreatedAt: values.authTokenCreatedAt!.toISOString(),
    authTokenRotatedAt: values.authTokenRotatedAt?.toISOString() || null,
    status: values.status,
    authorizedAt: values.authorizedAt?.toISOString() || null,
    authorizedById: values.showAuthorizedBy?.id || "",
    suspendedAt: values.suspendedAt?.toISOString() || null,
    suspendedById: values.showSuspendedBy?.id || "",
    suspensionReason: blankToNull(values.suspensionReason),
    enabled: values.enabled,
    allowedTenantIds: blankToNull(values.allowedTenantIds),
    proxyEnabled: values.proxyEnabled,
    proxyPort: values.proxyPort === undefined ? null : values.proxyPort,
    proxyProtocols: blankToNull(values.proxyProtocols),
    botEnabled: values.botEnabled,
    botMaxConcurrent:
      values.botMaxConcurrent === undefined ? null : values.botMaxConcurrent,
    workerEnabled: values.workerEnabled,
    publicIp: blankToNull(values.publicIp),
    location: blankToNull(values.location),
    countryCode: blankToNull(values.countryCode),
    lastHeartbeatAt: values.lastHeartbeatAt?.toISOString() || null,
    lastHeartbeatMetrics: safeJsonParse(
      values.lastHeartbeatMetrics,
      "lastHeartbeatMetrics"
    ),
    connectedAt: values.connectedAt?.toISOString() || null,
    disconnectedAt: values.disconnectedAt?.toISOString() || null,
    tunnelConnectedAt: values.tunnelConnectedAt?.toISOString() || null,
  };
}

export function getSatelliteMachineCreateVariables(
  values: SatelliteMachineValuesType
): SatelliteMachineCreateMutationVariables {
  return {
    name: values.name,
    machineCode: values.machineCode,
    satelliteType: values.satelliteType,
    region: blankToNull(values.region),
    certificateFingerprint: values.certificateFingerprint,
    certificateExpiresAt: values.certificateExpiresAt!.toISOString(),
    authTokenHash: values.authTokenHash,
    authTokenLastFour: values.authTokenLastFour,
    authTokenCreatedAt: values.authTokenCreatedAt!.toISOString(),
    authTokenRotatedAt: values.authTokenRotatedAt?.toISOString() || null,
    status: values.status,
    authorizedAt: values.authorizedAt?.toISOString() || null,
    authorizedById: values.showAuthorizedBy?.id || "",
    suspendedAt: values.suspendedAt?.toISOString() || null,
    suspendedById: values.showSuspendedBy?.id || "",
    suspensionReason: blankToNull(values.suspensionReason),
    enabled: values.enabled,
    allowedTenantIds: blankToNull(values.allowedTenantIds),
    proxyEnabled: values.proxyEnabled,
    proxyPort: values.proxyPort === undefined ? null : values.proxyPort,
    proxyProtocols: blankToNull(values.proxyProtocols),
    botEnabled: values.botEnabled,
    botMaxConcurrent:
      values.botMaxConcurrent === undefined ? null : values.botMaxConcurrent,
    workerEnabled: values.workerEnabled,
    publicIp: blankToNull(values.publicIp),
    location: blankToNull(values.location),
    countryCode: blankToNull(values.countryCode),
    lastHeartbeatAt: values.lastHeartbeatAt?.toISOString() || null,
    lastHeartbeatMetrics: safeJsonParse(
      values.lastHeartbeatMetrics,
      "lastHeartbeatMetrics"
    ),
    connectedAt: values.connectedAt?.toISOString() || null,
    disconnectedAt: values.disconnectedAt?.toISOString() || null,
    tunnelConnectedAt: values.tunnelConnectedAt?.toISOString() || null,
  };
}
